/**
 * TagSearchInput Component
 * 
 * Поисковый инпут для фильтрации тегов с debounce и клавиатурной навигацией
 * Поддерживает поиск по названию тегов и показ результатов в dropdown
 */

import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchResultsDropdown } from './SearchResultsDropdown';
import type { Tag } from '@/integrations/supabase/types';
import { useTranslation } from "react-i18next";

interface TagSearchInputProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
  // Search dropdown props
  searchResults?: Tag[];
  onSelectTag?: (tag: Tag) => void;
  onCreateTag?: (name: string) => void;
  showDropdown?: boolean;
  isSearchLoading?: boolean;
}

export const TagSearchInput: React.FC<TagSearchInputProps> = ({
  searchQuery,
  onSearchChange,
  placeholder,
  debounceMs = 300,
  className = "",
  searchResults = [],
  onSelectTag,
  onCreateTag,
  showDropdown = false,
  isSearchLoading = false
}) => {
  const { t } = useTranslation();
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search query
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onSearchChange(localQuery);
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [localQuery, onSearchChange, debounceMs]);

  // Sync external changes - only when searchQuery changes from outside, not localQuery changes
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalQuery(e.target.value);
  };

  const handleClear = () => {
    setLocalQuery('');
    onSearchChange('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Обработка Escape для очистки поиска
    if (e.key === 'Escape') {
      if (localQuery) {
        handleClear();
      } else {
        inputRef.current?.blur();
      }
      e.preventDefault();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder || t('records.tag_search.placeholder')}
          value={localQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="pl-10 pr-10"
          data-testid="tag-search-input"
        />
        {localQuery && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-muted"
            data-testid="clear-search-button"
            aria-label={t('records.tag_search.clear_search')}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Search results dropdown */}
      <SearchResultsDropdown
        searchQuery={localQuery}
        searchResults={searchResults}
        isVisible={showDropdown && localQuery.trim().length > 0}
        onSelectTag={onSelectTag}
        onCreateTag={onCreateTag}
        isLoading={isSearchLoading}
      />

      {/* Индикатор активного поиска */}
      {localQuery && !showDropdown && (
        <div className="absolute -bottom-6 left-0 text-xs text-muted-foreground">
          {t('records.tag_search.searching', { query: localQuery })}
        </div>
      )}
    </div>
  );
};